import type { OperationTypeDefinition, Archetype, Checkpoint } from '@/graph/domain/types';

/**
 * Map the old v5 `subType` values to our Archetype enum.
 * Built-in operations from GET /api/operations use subType;
 * custom operations from POST /api/customOperations/get use operationType.
 */
const SUBTYPE_TO_ARCHETYPE: Record<string, Archetype> = {
  transformation: 'TRANSFORM',
  measurement: 'MEASURE',
  transfer: 'TRANSFER',
  compound: 'COMPLEX',
};

function mapSubTypeToArchetype(subType?: string): Archetype | null {
  if (!subType) return null;
  return SUBTYPE_TO_ARCHETYPE[subType.toLowerCase()] ?? null;
}

/**
 * Recursively normalize a parameter and its children.
 * Handles both top-level parameters and nested child parameters (for group fields).
 */
function normalizeParameter(
  p: Record<string, unknown>,
  i: number,
): OperationTypeDefinition['parameters'][number] {
  // Children can live in `paramValues`, `children`, or `subMenu` depending on source shape
  const rawChildren = (p.paramValues ?? p.children ?? p.subMenu) as Record<string, unknown>[] | undefined;

  return {
    id: String(p.id ?? p._id ?? `param-${i}`),
    // Child params in Angular can use `fieldName` instead of `parameterName`
    parameterName: String(p.parameterName ?? p.fieldName ?? p.name ?? ''),
    fieldType: mapFieldType(String(p.fieldType ?? p.type ?? 'text')),
    order: Number(p.order ?? i),
    required: Boolean(p.required ?? false),
    uniqueAcrossWorkspace: Boolean(p.uniqueAcrossWorkspace ?? false),
    allowSpecialCharacters: Boolean(p.allowSpecialCharacters ?? true),
    allowMultiple: p.allowMultiple != null ? Boolean(p.allowMultiple) : undefined,
    parameterTags: Array.isArray(p.parameterTags) ? (p.parameterTags as string[]) : undefined,
    dropDownList: Array.isArray(p.dropDownList)
      ? (p.dropDownList as { id: string; label: string }[])
      : undefined,
    tableColumns: Array.isArray(p.tableColumns)
      ? (p.tableColumns as Array<{ id: string; header: string; required?: boolean }>)
      : undefined,
    rangeMin: p.rangeMin != null ? Number(p.rangeMin) : undefined,
    rangeMax: p.rangeMax != null ? Number(p.rangeMax) : undefined,
    pumpCount: p.pumpCount != null ? Number(p.pumpCount) : undefined,
    // Recursively normalize children (for group fields) — reads from `paramValues` or `children`
    children: Array.isArray(rawChildren)
      ? rawChildren.map((child, childIdx) => normalizeParameter(child, childIdx))
      : undefined,
    safeKey: p.safeKey as string | undefined,
  };
}

/**
 * Normalize a raw operation (built-in or custom) from the backend into the
 * canonical `OperationTypeDefinition` shape. Heuristics handle both the legacy
 * flat `STEP_PARAMETERS` catalog and the richer `CustomOperation` model.
 */
export function normalizeOperation(raw: Record<string, unknown>): OperationTypeDefinition {
  const id = String(raw._id ?? raw.id ?? '');
  const uid = String(raw.uid ?? raw.id ?? id);

  // Archetype: prefer operationType (custom ops), fall back to subType mapping (built-in)
  let archetype: Archetype | null = null;
  if (raw.operationType && typeof raw.operationType === 'string') {
    archetype = (raw.operationType as Archetype) ?? null;
  }
  if (!archetype && raw.subType && typeof raw.subType === 'string') {
    archetype = mapSubTypeToArchetype(raw.subType as string);
  }

  // Parameters: custom ops store in `parameters`, built-in in `submenu`
  const rawParams = (raw.parameters ?? raw.submenu ?? []) as Record<string, unknown>[];
  const parameters = rawParams.map((p, i) => normalizeParameter(p, i));

  const checkpoints = Array.isArray(raw.checkpoints)
    ? (raw.checkpoints as Checkpoint[])
    : [];

  const opConfig = raw.operationTypeConfig as {
    allowYield?: boolean;
    allowMultipleInputs?: boolean;
    allowMultipleOutputs?: boolean;
    allowIOMapping?: boolean;
  } | undefined;

  return {
    id,
    uid,
    operationName: String(raw.operationName ?? raw.name ?? ''),
    operationType: archetype,
    subType: raw.subType as string | undefined,
    display: raw.display !== false,
    isArchived: Boolean(raw.isArchived),
    isSoftDeleted: Boolean(raw.isSoftDeleted),
    isLocked: Boolean(raw.isLocked),
    versionNumber: Number(raw.versionNumber ?? 1),
    isCurrentVersion: raw.isCurrentVersion !== false,
    versions: Array.isArray(raw.versions) ? raw.versions as string[] : [],
    durationRequired: Boolean(raw.durationRequired),
    durationAllow: raw.durationAllow !== false,
    canEditCheckpoints: Boolean(raw.canEditCheckpoints),
    translationTemplate: String(raw.translationTemplate ?? ''),
    parameters,
    checkpoints,
    operationTypeConfig: {
      allowYield: Boolean(opConfig?.allowYield),
      allowMultipleInputs: Boolean(opConfig?.allowMultipleInputs),
      allowMultipleOutputs: Boolean(opConfig?.allowMultipleOutputs),
      allowIOMapping: Boolean(opConfig?.allowIOMapping),
    },
    workspaceId: String(raw.workspaceId ?? ''),
    addedBy: (raw.addedBy as OperationTypeDefinition['addedBy']) ?? { id: '', email: '', name: '' },
    createdOn: String(raw.createdOn ?? ''),
    updatedOn: String(raw.updatedOn ?? ''),
  };
}

/**
 * Map old v5 field types (unit-value, radio, select, editable-select) to canonical ParameterFieldType.
 */
function mapFieldType(raw: string): OperationTypeDefinition['parameters'][number]['fieldType'] {
  switch (raw) {
    case 'unit-value':
      return 'units';
    case 'radio':
    case 'select':
    case 'editable-select':
      return 'dropdown';
    case 'text':
    case 'number':
    case 'textNumber':
    case 'dropdown':
    case 'group':
    case 'gradientProgram':
    case 'table':
    case 'date':
    case 'user':
    case 'file':
    case 'units':
    case 'range':
    case 'linkEntity':
      return raw;
    default:
      return 'text';
  }
}
